// src/pages/healthcare/appointments/appDrawerComponents/GeneralTab.tsx
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, InputNumber, Modal, Select, DatePicker, AutoComplete, Avatar, Space, Tag, Tooltip, Typography } from "antd";
import SectionCard from "../../../../components/shared/SectionCard";
import { UserOutlined, ClockCircleOutlined, SyncOutlined, InfoCircleOutlined, PlusOutlined, DeleteOutlined, UsergroupAddOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DateTimeUtils } from "../../../../utils/userPrefs/DateTimeUtils";

// API imports
import { Appointment, AppointmentPractitioner, categories, consultStatuses } from "../../../../types/appointments/AppointmentTypes";
import { fetchPatients } from "../../../../api/healthcare/PatientApi";
import { fetchAppointmentTypes, fetchAppointmentTypesWithoutPractitioners } from "../../../../api/healthcare/Appointment/AppointmentApi";
import { AppointmentType } from "../../../../types/appointments/AppointmentTypes";


// Types
import { Patient, defaultPatient } from "../../../../types/healthcare/PatientTypes";
import { TeamMember } from "../../../../types/admin/TeamTypes";

// Components
import EditPatientModal from "../../patient/PatientModal";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";
import { isPhoneSearchTerm } from "../../../../utils/healthcare/phoneSearch";
import { generateConsultationLink, generateRoomId } from "../../../../utils/consult/consultationLink";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;

interface GeneralTabProps {
	isLock: boolean;
	appointment: Appointment;
	setAppointment: React.Dispatch<React.SetStateAction<Appointment>>;
	selectedPatient: Patient;
	setSelectedPatient: React.Dispatch<React.SetStateAction<Patient>>;
	selectedTeamMember: TeamMember | null;
	setSelectedTeamMember: React.Dispatch<React.SetStateAction<TeamMember | null>>;
	teamMembers: TeamMember[];
	secondaryPractitioners?: AppointmentPractitioner[];
	onAddSecondaryPractitioner?: (practitionerId: UserUuid, timingPosition: 'before' | 'during' | 'after', timingMinutes: number) => Promise<void>;
	onRemoveSecondaryPractitioner?: (practitionerId: UserUuid) => Promise<void>;
	appointmentTypes?: AppointmentType[];
	setAppointmentTypes?: React.Dispatch<React.SetStateAction<AppointmentType[]>>;
	selectedAppointmentType?: AppointmentType | null;
	setSelectedAppointmentType?: React.Dispatch<React.SetStateAction<AppointmentType | null>>;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ isLock, appointment, setAppointment, selectedPatient, selectedTeamMember, setSelectedPatient, setSelectedTeamMember, teamMembers, secondaryPractitioners = [], onAddSecondaryPractitioner, onRemoveSecondaryPractitioner, appointmentTypes = [], setAppointmentTypes, selectedAppointmentType, setSelectedAppointmentType }) => {
	const [patients, setPatients] = useState<Patient[]>([]);
	const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
	const [patientInputValue, setPatientInputValue] = useState<string>("");
	const [newPatient, setNewPatient] = useState<Patient | null>(null);
	const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
	const [isCreatingPatient, setIsCreatingPatient] = useState<boolean>(false);
	// Shown in the picker's dropdown when a search returns nothing AND the create-new-patient
	// shortcut is deliberately withheld (phone terms — see runPatientSearch).
	const [patientNotFoundText, setPatientNotFoundText] = useState<string | null>(null);

	const [isLoadingTypes, setIsLoadingTypes] = useState<boolean>(false);
	const PATIENTS_PER_PAGE = loadUserPreferences().tableRowsPerPage;
	const [addSecondaryVisible, setAddSecondaryVisible] = useState(false);
	const [addingSecondary, setAddingSecondary] = useState(false);
	const [editingSecondary, setEditingSecondary] = useState<AppointmentPractitioner | null>(null);
	const [newSecondaryId, setNewSecondaryId] = useState<UserUuid | undefined>(undefined);
	const [newSecondaryPosition, setNewSecondaryPosition] = useState<'before' | 'during' | 'after'>('during');
	const [newSecondaryMinutes, setNewSecondaryMinutes] = useState(0);

	const openSecondaryModal = (sp?: AppointmentPractitioner) => {
		if (sp) {
			setEditingSecondary(sp);
			setNewSecondaryId(sp.practitioner_id);
			setNewSecondaryPosition(sp.timing_position);
			setNewSecondaryMinutes(sp.timing_minutes);
		} else {
			setEditingSecondary(null);
			setNewSecondaryId(undefined);
			setNewSecondaryPosition('during');
			setNewSecondaryMinutes(0);
		}
		setAddSecondaryVisible(true);
	};

	// Load the full type list on mount (existing appointments too) so the type can be changed
	// after booking, not just at creation.
	useEffect(() => {
		loadAppointmentTypes();
	}, []);

	// Set selected appointment type when appointment changes
	useEffect(() => {
		if (appointment.appointment_type_id && appointmentTypes.length > 0) {
			const type = appointmentTypes.find((t) => t.id === appointment.appointment_type_id);
			setSelectedAppointmentType(type || null);
		}
	}, [appointment.appointment_type_id, appointmentTypes]);

	// Load appointment types
	const loadAppointmentTypes = async () => {

		setIsLoadingTypes(true);
		try {
			const types = await fetchAppointmentTypesWithoutPractitioners();
			const active = types.filter((t) => t.is_active);
			// Keep the appointment's current type selectable even if it has since been deactivated.
			if (appointment.appointment_type && !active.some((t) => t.id === appointment.appointment_type!.id)) {
				active.push(appointment.appointment_type);
			}
			setAppointmentTypes(active);
		} catch (error) {
			console.error("Error loading appointment types:", error);
		} finally {
			setIsLoadingTypes(false);
		}
	};

	useEffect(() => {
		if (appointment.patient_id === "" || selectedPatient.id === "") {
			getPatients();
			setPatientInputValue("");
		} else {
			const updatedPatients = patients.find((p) => p.id === selectedPatient.id) ? patients : [selectedPatient, ...patients];
			setPatients(updatedPatients);
			setFilteredPatients(updatedPatients);
			setPatientInputValue(`${selectedPatient.first_name} ${selectedPatient.last_name}`);
		}
	}, [selectedPatient]);

	const getPatients = async (search?: string) => {
		try {
			const sortField = "first_name";
			const sortDirection = "asc";
			const { data } = await fetchPatients(1, PATIENTS_PER_PAGE, search, sortField, sortDirection);
			setPatients(data);
			setFilteredPatients(data);
		} catch (err) {
			console.error("Error fetching patients:", err);
		}
	};

	/**
	 * Debounced patient lookup.
	 */
	const patientSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Drop any in-flight debounce so an unmounted drawer never fires a request.
	useEffect(() => {
		return () => {
			if (patientSearchTimer.current) clearTimeout(patientSearchTimer.current);
		};
	}, []);

	const handlePatientSearch = (searchText: string) => {
		setPatientInputValue(searchText);
		if (patientSearchTimer.current) clearTimeout(patientSearchTimer.current);
		patientSearchTimer.current = setTimeout(() => {
			void runPatientSearch(searchText);
		}, 300);
	};

	const runPatientSearch = async (searchText: string) => {
		setPatientNotFoundText(null);

		if (searchText === "") {
			try {
				const { data } = await fetchPatients(1, PATIENTS_PER_PAGE, "", "first_name", "asc");
				setFilteredPatients(data);
			} catch (err) {
				console.error("Error fetching patients:", err);
				setFilteredPatients([]);
			}
			return;
		}

		try {
			const { data } = await fetchPatients(1, PATIENTS_PER_PAGE, searchText, "first_name", "asc");
			let updatedPatients = [...data];
			if (data.length === 0) {
				if (isPhoneSearchTerm(searchText)) {
					// A phone search that matched nothing must NOT offer to create a patient
					setPatientNotFoundText("No patient found with that number");
				} else {
					updatedPatients.push({
						id: -1,
						first_name: `Create new patient: "${searchText}"`,
						last_name: "",
						isNewItem: true,
					} as unknown as Patient);
				}
			}
			setFilteredPatients(updatedPatients);
		} catch (err) {
			console.error("Error fetching patients:", err);
			setFilteredPatients([]);
		}
	};

	const handlePatientSelect = (value: string, option: any) => {
		const patient = option.patient;

		if ((patient as any)?.isNewItem) {
			if (isCreatingPatient) return;
			setIsCreatingPatient(true);

			const match = patient.first_name.match(/Create new patient: "(.+)"/);
			const searchText = match ? match[1] : patientInputValue;
			let parts = searchText.split(" ");
			const firstName = parts[0];
			const lastName = parts.slice(1).join(" ");

			let newPat: Patient = { ...defaultPatient };
			newPat.first_name = firstName;
			newPat.last_name = lastName;

			setNewPatient(newPat);
			setCreateModalOpen(true);
			setPatientInputValue("");
			return;
		}

		if (patient && typeof patient === "object") {
			setSelectedPatient(patient);
			setPatientInputValue(`${patient.first_name} ${patient.last_name}`);
			setAppointment((prev) => ({
				...prev,
				patient_id: patient.id,
				title: `${patient.first_name} ${patient.last_name}`,
			}));
		}
	};

	const handlePatientClear = () => {
		setSelectedPatient(defaultPatient);
		setPatientInputValue("");
		setPatientNotFoundText(null);
		setAppointment((prev) => ({
			...prev,
			patient_id: "",
			title: "",
		}));
		getPatients();
	};

	const handleTeamMemberChange = (value: UserUuid) => {
		const member = teamMembers.find((tm) => tm.id === value);
		setSelectedTeamMember(member || null);
		setAppointment((prev) => ({
			...prev,
			practitioner_id: value,
		}));
	};

	const handleAppointmentTypeChange = (typeId: number) => {
		const type = appointmentTypes.find((t) => t.id === typeId);
		if (!type) return;

		setSelectedAppointmentType(type);

		// Update appointment with type details
		setAppointment((prev) => {
			const updated = {
				...prev,
				appointment_type_id: typeId,
				category: type.category || prev.category,
			};

			// Auto-set location based on telehealth_enabled
			if (type.telehealth_enabled && !prev.location) {
				updated.location = "Online";
				updated.consultation_link = generateConsultationLink();
			} else if (!type.telehealth_enabled && !prev.location) {
				updated.location = "In Person";
				updated.room_number = generateRoomId();
			}

			// Auto-calculate end time if start time exists
			if (prev.start_time && type.duration_minutes) {
				const endTime = new Date(prev.start_time);
				endTime.setMinutes(endTime.getMinutes() + type.duration_minutes);
				updated.end_time = endTime;
			}

			return updated;
		});
	};

	const handleLocationChange = (value: string) => {
		setAppointment((prev) => ({
			...prev,
			location: value,
			room_number: value === "In Person" ? generateRoomId() : "",
			consultation_link: value === "Online" ? generateConsultationLink() : "",
		}));
	};

	const handleCreatePatientClose = async (createdPatient?: Patient) => {
		setCreateModalOpen(false);
		setNewPatient(null);
		setIsCreatingPatient(false);

		if (createdPatient && createdPatient.id && createdPatient.id !== "") {
			setSelectedPatient(createdPatient);
			setPatientInputValue(`${createdPatient.first_name} ${createdPatient.last_name}`);
			setAppointment((prev) => ({
				...prev,
				patient_id: createdPatient.id,
				title: `${createdPatient.first_name} ${createdPatient.last_name}`,
			}));
			const updatedPatients = [createdPatient, ...patients];
			setPatients(updatedPatients);
			setFilteredPatients(updatedPatients);
		} else {
			setPatientInputValue("");
			getPatients();
		}
	};

	const patientOptions = filteredPatients.map((patient, index) => ({
		value: (patient as any).isNewItem ? `new-patient-${index}` : `${patient.first_name} ${patient.last_name} (ID: ${patient.id})`,
		label: (patient as any).isNewItem ? (
			<div style={{ fontStyle: "italic", color: "var(--mg-primary)" }}>
				<i className="pi pi-plus-circle" style={{ marginRight: 8 }}></i>
				{patient.first_name}
			</div>
		) : (
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: "var(--mg-primary-bg)", color: "var(--mg-primary)" }} />
				<div>
					<div style={{ fontSize: 12, fontWeight: 500 }}>
						{patient.first_name} {patient.last_name}
					</div>
					{/*
						Phone sits on the same secondary line as email so a match found BY phone can be
						verified (86d3nzmg1). Each part is guarded independently: `phone` is typed
						non-nullable but is "" on real rows, and a patient with neither renders no
						secondary line at all rather than an empty one.
					*/}
					{(patient.email || patient.phone) && (
						<div style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>
							{patient.email}
							{patient.email && patient.phone ? " · " : ""}
							{patient.phone}
						</div>
					)}
				</div>
			</div>
		),
		patient: patient,
	}));
	
	const chosenType = appointmentTypes.find((t) => t.id === appointment.appointment_type_id) ?? selectedAppointmentType ?? null;

	const isClinikoAppointment = appointment.created_in_cliniko || !!appointment.cliniko_id;

	return (
		<Space direction="vertical" size={12} style={{ width: "100%" }}>
			{/* Appointment Type Selection */}
			<SectionCard title="Appointment Type">
				<div style={{ marginBottom: 12 }}>
					<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>
						Select Appointment Type
						<Tooltip title="Choose the type of appointment. Duration and settings will be automatically applied.">
							<InfoCircleOutlined style={{ marginLeft: 4, color: "#999", fontSize: 11 }} />
						</Tooltip>
					</label>
					<Select
						value={appointment.appointment_type_id}
						onChange={handleAppointmentTypeChange}
						size="small"
						style={{ width: "100%" }}
						placeholder="Select appointment type"
						loading={isLoadingTypes}
						disabled={isClinikoAppointment}
						data-testid="appointments-type-select"
						showSearch
						filterOption={(input, option) => {
							const type = appointmentTypes.find((t) => t.id === option?.value);
							if (!type) return false;
							return type.name.toLowerCase().includes(input.toLowerCase()) || (type.category?.toLowerCase().includes(input.toLowerCase()) ?? false);
						}}
						options={appointmentTypes.map((type) => ({
							label: (
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<div
											style={{
												width: 12,
												height: 12,
												borderRadius: "50%",
												backgroundColor: type.color || "#1890ff",
												flexShrink: 0,
											}}
										/>
										<div>
												<div style={{ fontSize: 12, fontWeight: 500 }}>{type.name}</div>
										</div>
									</div>
									<div style={{ fontSize: 11, color: "var(--mg-text-disabled)" }}>
										{type.duration_minutes}min
										{type.cliniko_id && <SyncOutlined style={{ marginLeft: 4, color: "#1890ff" }} />}
									</div>
								</div>
							),
							value: type.id,
						}))}
					/>
				</div>
				{chosenType?.has_screening && (
					<Alert
						type="info"
						showIcon
						style={{ marginBottom: 0 }}
						data-testid="appointments-screening-notice"
						message="This appointment type has a pre-booking questionnaire. A booking you create here skips it, so ask the questions before the consult."
					/>
				)}
			</SectionCard>

			{/* Appointment Information */}
			<SectionCard title="Appointment Details">
				<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Category</label>
						<Select
							value={appointment.category}
							onChange={(value) => setAppointment((prev) => ({ ...prev, category: value }))}
							options={categories.map((c) => ({ label: c, value: c }))}
							size="small"
							style={{ width: "100%" }}
							allowClear
							disabled={isClinikoAppointment}
						/>
					</div>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Status</label>
						<Select
							value={appointment.status}
							onChange={(value) => setAppointment((prev) => ({ ...prev, status: value }))}
							options={consultStatuses.map((s) => ({ label: s, value: s }))}
							size="small"
							style={{ width: "100%" }}
							allowClear
							data-testid="appointments-status-select"
						/>
					</div>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Location</label>
						<Select
							value={appointment.location}
							onChange={handleLocationChange}
							options={appointmentTypes.map((type) => ({ label: type, value: type }))}
							size="small"
							style={{ width: "100%" }}
							allowClear
							disabled={isClinikoAppointment || (selectedAppointmentType && !selectedAppointmentType.telehealth_enabled && appointment.location === "Online")}
						/>
					</div>
				</div>
			</SectionCard>

			{/* Schedule */}
			<SectionCard
				title={<Space>Schedule <ClockCircleOutlined style={{ fontSize: 14 }} /></Space>}
				extra={<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>{DateTimeUtils.getUserTimezone()} ({DateTimeUtils.getTimezoneOffset()})</span>}
			>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Start Time</label>
						<DatePicker
							showTime={{ format: "HH:mm", minuteStep: 15 }}
							format="DD/MM/YYYY HH:mm"
							value={appointment.start_time ? dayjs(appointment.start_time) : null}
							data-testid="appointments-start-time"
							onChange={(date) => {
								const value = date ? date.toDate() : undefined;
								setAppointment((prev) => {
									const updated = { ...prev, start_time: value };

									// Auto-calculate end time based on appointment type duration
									if (value) {
										if (selectedAppointmentType?.duration_minutes) {
											const endTime = new Date(value);
											endTime.setMinutes(endTime.getMinutes() + selectedAppointmentType.duration_minutes);
											updated.end_time = endTime;
										} else if (!prev.end_time) {
											const endTime = new Date(value);
											endTime.setMinutes(endTime.getMinutes() + 30);
											updated.end_time = endTime;
										}
									}

									return updated;
								});
							}}
							size="small"
							style={{ width: "100%" }}
							disabled={isClinikoAppointment}
						/>
					</div>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>End Time</label>
						<DatePicker
							showTime={{ format: "HH:mm", minuteStep: 15 }}
							format="DD/MM/YYYY HH:mm"
							value={appointment.end_time ? dayjs(appointment.end_time) : null}
							onChange={(date) => {
								const value = date ? date.toDate() : undefined;
								setAppointment((prev) => ({ ...prev, end_time: value }));
							}}
							size="small"
							style={{ width: "100%" }}
							disabled={isClinikoAppointment}
						/>
						{selectedAppointmentType && appointment.start_time && appointment.end_time && (
							<div style={{ fontSize: 10, color: "var(--mg-text-disabled)", marginTop: 4 }}>Duration: {Math.round((appointment.end_time.getTime() - appointment.start_time.getTime()) / 60000)} min</div>
						)}
					</div>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							border: "1px dashed var(--border-color)",
							borderRadius: 6,
							padding: 8,
						}}
					>
						<Space>
							<SyncOutlined style={{ color: "var(--mg-primary)" }} />
							<span style={{ fontSize: 11, color: "#666" }}>Recurring options coming soon</span>
						</Space>
					</div>
				</div>
			</SectionCard>

			{/* Patient & Provider */}
			<SectionCard title="Patient & Provider">
				<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Select Patient</label>
						<AutoComplete
							value={patientInputValue}
							options={patientOptions}
							onSearch={handlePatientSearch}
							onSelect={handlePatientSelect}
							onClear={handlePatientClear}
							notFoundContent={patientNotFoundText ? <Text type="secondary" style={{ fontSize: 12 }}>{patientNotFoundText}</Text> : null}
							placeholder="Search name, email or phone…"
							size="small"
							style={{ width: "100%" }}
							allowClear
							disabled={isLock}
						/>
					</div>
					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Select Provider</label>
						<Select
							value={selectedTeamMember?.id || undefined}
							onChange={handleTeamMemberChange}
							size="small"
							style={{ width: "100%" }}
							allowClear
							disabled={isLock || isClinikoAppointment}
							showSearch
							filterOption={(input, option) => {
								const member = teamMembers.find((tm) => tm.id === option?.value);
								if (!member) return false;
								const searchText = `${member.first_name} ${member.last_name}`.toLowerCase();
								return searchText.includes(input.toLowerCase());
							}}
							options={teamMembers.map((member) => ({
								label: (
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: "var(--mg-success-bg)", color: "var(--mg-success)" }} />
										<span style={{ fontSize: 12 }}>
											{member.first_name} {member.last_name}
										</span>
									</div>
								),
								value: member.id,
							}))}
						/>
					</div>
				</div>

					{/* Secondary Practitioners */}
					{appointment.id !== 0 && (
						<div style={{ marginTop: 12 }}>
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
								<label style={{ fontSize: 12 }}>
									<UsergroupAddOutlined style={{ marginRight: 4 }} />
									Secondary Practitioners
								</label>
								{!isLock && onAddSecondaryPractitioner && (
									<Button size="small" icon={<PlusOutlined />} onClick={() => openSecondaryModal()}>
										Add
									</Button>
								)}
							</div>

							{secondaryPractitioners.length === 0 ? (
								<Text type="secondary" style={{ fontSize: 11 }}>No secondary practitioners assigned</Text>
							) : (
								<Space direction="vertical" style={{ width: '100%' }} size={4}>
									{secondaryPractitioners.map(sp => {
										const member = teamMembers.find(tm => tm.id === sp.practitioner_id);
										return (
											<div key={sp.practitioner_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--mg-bg-subtle)', borderRadius: 4, border: '1px solid var(--mg-border)' }}>
												<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
													<Avatar icon={<UserOutlined />} size={18} style={{ backgroundColor: 'var(--mg-info-bg)', color: 'var(--mg-info)' }} />
													<Text style={{ fontSize: 12 }}>{member ? `${member.first_name} ${member.last_name}` : `Practitioner #${sp.practitioner_id}`}</Text>
													<Tag color="default" style={{ fontSize: 10, margin: 0 }}>
														{sp.timing_position}{sp.timing_minutes > 0 ? ` +${sp.timing_minutes}m` : ''}
													</Tag>
												</div>
												{!isLock && (
													<Space size={2}>
														{onAddSecondaryPractitioner && (
															<Button size="small" icon={<EditOutlined />} onClick={() => openSecondaryModal(sp)} style={{ border: 'none', background: 'transparent' }} />
														)}
														{onRemoveSecondaryPractitioner && (
															<Button danger size="small" icon={<DeleteOutlined />} onClick={() => onRemoveSecondaryPractitioner(sp.practitioner_id)} style={{ border: 'none', background: 'transparent' }} />
														)}
													</Space>
												)}
											</div>
										);
									})}
								</Space>
							)}
						</div>
					)}

			</SectionCard>
			{newPatient && createModalOpen && <EditPatientModal isOpen={createModalOpen} onClose={handleCreatePatientClose} patient={newPatient} />}

			<Modal
				title={editingSecondary ? "Change Secondary Practitioner" : "Add Secondary Practitioner"}
				open={addSecondaryVisible}
				onCancel={() => { setAddSecondaryVisible(false); setEditingSecondary(null); }}
				confirmLoading={addingSecondary}
				onOk={async () => {
					if (!newSecondaryId || !onAddSecondaryPractitioner) return;
					setAddingSecondary(true);
					try {
						if (editingSecondary && onRemoveSecondaryPractitioner && editingSecondary.practitioner_id !== newSecondaryId) {
							await onRemoveSecondaryPractitioner(editingSecondary.practitioner_id);
						}
						await onAddSecondaryPractitioner(newSecondaryId, newSecondaryPosition, newSecondaryMinutes);
						setAddSecondaryVisible(false);
						setEditingSecondary(null);
						setNewSecondaryId(undefined);
						setNewSecondaryPosition('during');
						setNewSecondaryMinutes(0);
					} finally {
						setAddingSecondary(false);
					}
				}}
				okButtonProps={{ disabled: !newSecondaryId }}
			>
				<Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
					<div>
						<label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Practitioner</label>
						<Select
							style={{ width: '100%' }}
							placeholder="Select practitioner"
							value={newSecondaryId}
							onChange={setNewSecondaryId}
							showSearch
							filterOption={(input, option) => {
								const member = teamMembers.find(tm => tm.id === option?.value);
								if (!member) return false;
								return `${member.first_name} ${member.last_name}`.toLowerCase().includes(input.toLowerCase());
							}}
							options={teamMembers
								.filter(tm => tm.id !== selectedTeamMember?.id && (!secondaryPractitioners.find(sp => sp.practitioner_id === tm.id) || tm.id === editingSecondary?.practitioner_id))
								.map(tm => ({ value: tm.id, label: `${tm.first_name} ${tm.last_name}` }))}
						/>
					</div>
					<div>
						<label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Timing Position</label>
						<Select
							style={{ width: '100%' }}
							value={newSecondaryPosition}
							onChange={setNewSecondaryPosition}
							options={[
								{ value: 'before', label: 'Before primary practitioner' },
								{ value: 'during', label: 'Concurrent with primary practitioner' },
								{ value: 'after', label: 'After primary practitioner' },
							]}
						/>
					</div>
					<div>
						<label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Offset Minutes</label>
						<InputNumber min={0} step={5} style={{ width: '100%' }} value={newSecondaryMinutes} onChange={v => setNewSecondaryMinutes(v ?? 0)} />
					</div>
				</Space>
			</Modal>
		</Space>
	);
};

export default GeneralTab;
